// frontend/src/components/librarian/ui/LibButton.tsx
import React from 'react';
import { cn } from '@/lib/utils';

/**
 * The staff app's action button.
 *
 * This exists because the gold primary button was hand-written inline ~62 times
 * with drifting geometry (h-8/h-9/h-10, text-[12px]/[13px]/sm) and a hardcoded
 * `text-[var(--lib-navy)]` foreground. `--lib-navy` flips to gold on dark
 * grounds, so every one of those buttons rendered gold-on-gold at 1.19:1.
 *
 * The fill/foreground pair lives here now: `--lib-on-gold` is theme-invariant,
 * so the contrast cannot be broken by a future dark-mode edit. Reach for this
 * instead of re-typing the classes.
 *
 * Note this is deliberately separate from `components/ui/button.tsx`: that one
 * is the shadcn primitive on the HSL `--primary`/`--secondary` token system,
 * which the staff app does not use.
 */

const SIZES = {
  sm: 'h-8 px-3 text-[12px] gap-1.5',
  md: 'h-9 px-4 text-[13px] gap-2',
  lg: 'h-10 px-5 text-sm gap-2',
  /** No geometry. For the handful of call sites with bespoke padding that
   *  predate this component: they pass their own h-/px-/py- via className. */
  none: '',
} as const;

const VARIANTS = {
  /** Gold fill. The only primary action on a view. */
  primary: 'bg-[var(--lib-gold)] text-[var(--lib-on-gold)] hover:brightness-105',
  /** Outlined. Uses the darkened gold so both the label and the border,
   *  which is the control's only boundary, clear contrast on light grounds. */
  outline:
    'border border-[var(--lib-gold-text)] text-[var(--lib-gold-text)] hover:bg-[var(--lib-surface-2)]',
  /** Low-emphasis action that still needs to read as interactive. */
  ghost:
    'text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] hover:bg-[var(--lib-surface-2)]',
  /** Destructive. */
  danger: 'bg-[var(--lib-danger)] text-white hover:brightness-110',
} as const;

export interface LibButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: keyof typeof SIZES;
  variant?: keyof typeof VARIANTS;
}

const LibButton = React.forwardRef<HTMLButtonElement, LibButtonProps>(
  // `type` is deliberately NOT defaulted to "button". Several call sites sit
  // inside forms and rely on the native implicit-submit behaviour; defaulting
  // it here would silently stop those forms submitting. Pass it explicitly.
  ({ size = 'md', variant = 'primary', className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-display font-semibold',
        'whitespace-nowrap transition-all',
        'disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none',
        '[&_svg]:shrink-0',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...props}
    />
  ),
);

LibButton.displayName = 'LibButton';

export default LibButton;
